<script setup lang="ts">
import type { PropType } from 'vue';

import type { AuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntityWithNegotiation>, required: true },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const localePath = useLocalePath();
const { negotiation, isBuyer, isUnderSellerReview } = useAuctionNegotiation(() => auction);

// DATA
const translationKey = computed<TranslationKey>(() => {
    if (isUnderSellerReview.value)
        return isBuyer.value ? 'auctionNegotiation.underSellerReviewNoticeForBuyer' : 'auctionNegotiation.underSellerReviewNoticeForSeller';

    return isBuyer.value ? 'auctionNegotiation.openNoticeForBuyer' : 'auctionNegotiation.openNoticeForSeller';
});

const finalOfferLabel = computed(() =>
    tt(
        isBuyer.value
            ? 'auctionNegotiation.underSellerReviewNoticeFinalOfferForBuyer'
            : 'auctionNegotiation.underSellerReviewNoticeFinalOfferForSeller'
    )
);
</script>

<template>
    <div class="flex items-center gap-2">
        <UIcon class="text-error-600 shrink-0" name="lucide:info" />
        <i18n-t scope="global" tag="p" class="text-dimmed text-xs lg:text-base" :keypath="translationKey">
            <template v-if="isUnderSellerReview" #finalOffer>
                <span class="text-default">{{ finalOfferLabel }}</span>
            </template>

            <template #countdown>
                <BaseCountdown :expires-at="negotiation.expiresAt" class="text-primary-500" />
            </template>

            <template #moreInfoLink>
                <NuxtLink :to="localePath({ name: 'faq', hash: '#negotiation' })" class="text-default whitespace-nowrap underline" target="_blank">
                    {{ tt('auctionNegotiation.moreInfoLink') }}
                </NuxtLink>
            </template>
        </i18n-t>
    </div>
</template>
